// ShipView has been decomposed into a Views/Ship/ folder. This root file stays a thin
// composer (keeps the ShipView component + its state/effects/gating) and re-exports
// the public API so existing `./ShipView` import paths (dozens of importers across the
// app) keep working unchanged.

import { useEffect, useMemo, useState, ReactNode } from "react"
import { GetApp } from "../App"
import { GetShip, GetShipContext } from "../App/Context"
import { Router, RouteParams, useRouter } from "../App/Routes"
import { useOverlay } from "../App/useOverlay"
import { useSubscription } from "../App/useSubscription"
import { ILogbookAppNMEAConnectionStatus, ILogbookAppStatusResult, ILogbookSyncStatus } from "../Types"
import { shipChannel, Subscription } from "../Types/Generated/LogbookApi/Subscriptions"
import { AppRoutes, GetAppView } from "./AppView"
import { CruiseView } from "./CruiseView"
import { CruiseCalendarView } from "./CruiseCalendarView"
import { FocusView } from "./FocusView"
import { InstrumentsView } from "./InstrumentsView"
import { PrintMode, PrintRoot, PrintView } from "./PrintView"
import { ShipEditView } from "./ShipEditView"
import { ShipImportView } from "./ShipImportView"
import { ShipOverlayContentView, ShipViewOverlayContent, ShipViewOverlayOptions } from "./Ship/OverlayContentView"
import { ShipSyncStatusView } from "./Ship/SyncStatusView"
import { Loading } from "../Components/Loading"
import { ShipRoutes, ShipViewRoutes } from "./Ship/Router"
import { ShipViewState, ShipViewContext } from "./Ship/Context"
import { ShipViewProps } from "./Ship/Params"
import { RecordingClockWarning } from "./Ship/RecordingClockWarning"

export function ShipView({ route, shipId, users, actingShipUser, setActingShipUser }: ShipViewProps) {
  const app = GetApp()!;
  const _t = app.getT("ShipView");
  const ship = GetShip();
  const { requireUserLevel } = GetShipContext();
  const { loadShips: _loadShips, navigateApp, selectShip: _selectShip } = GetAppView();
  const [instrumentsMinized, setInstrumentsMinized] = useState<boolean>(true);
  const [printMode, setPrintMode] = useState<PrintMode>(PrintMode.none);
  const [printRoot, setPrintRoot] = useState<PrintRoot | undefined>(undefined);
  const [printTripId, setPrintTripId] = useState<number | undefined>(undefined);
  const [printDayId, setPrintDayId] = useState<number | undefined>(undefined);
  const [printReturnRoute, setPrintReturnRoute] = useState<string>(ShipRoutes.calendar);
  const [printReturnParams, setPrintReturnParams] = useState<RouteParams>({});
  const [status, setStatus] = useState<ILogbookAppStatusResult | undefined>(app.status);

  useEffect(() => {
    const channel = shipChannel(String(shipId));
    app.socket?.subscribeChannel(channel);
    // #1716-class regression: without this cleanup, switching ships (or leaving
    // ShipView entirely) left the previous ship's channel subscribed forever --
    // `app-base`'s channel set only grows, so a user who has ever opened another
    // ship keeps receiving its live broadcasts. Since `ShipUser` ids are only
    // unique within their own ship (`Connections/Ship.ts`), a same-id broadcast
    // from that stale, backgrounded ship can silently overwrite the entity
    // currently serving the ship actually open, corrupting its `shipId` field and
    // (per `AppView.tsx`'s eviction effect, keyed off exactly that field) reading
    // as the acting user's own access having been revoked.
    return () => {
      app.socket?.unsubscribeChannel(channel);
    };
  }, [shipId]);

  const navigateShip = useMemo(() => {
    return Router.navigate(ShipViewRoutes, app.updateRoute, `ships/${shipId}`);
  }, []);

  useSubscription((status: ILogbookAppStatusResult, _type: string) => {
    setStatus(status);
  }, Subscription.AppStatus);

  // #1204: a momentary NMEA blip (e.g. a mobile-network drop at sea) must not flicker
  // the instruments panel in and out. Keep it mounted through any transient state
  // (connectPending/connectionFailed/waitingForData/verifyingConnection/connected) —
  // it only unmounts once NMEA has never been configured or was deliberately
  // disconnected. `InstrumentsView` shows its own warning icon when not actually
  // live (`nmeaConnected` below), driven by the real-time `connected` flag.
  const nmeaConnectionStatus = status?.nmea?.connectionStatus;
  const nmeaConfigured = nmeaConnectionStatus !== undefined
    && nmeaConnectionStatus !== ILogbookAppNMEAConnectionStatus.notConfigured
    && nmeaConnectionStatus !== ILogbookAppNMEAConnectionStatus.disconnected;
  const nmeaConnected = status?.nmea?.connected ?? false;

  const [content, shipRouteName, routeParams] = useRouter<ReactNode>(route, ShipViewRoutes, (router) => {
    router.on(ShipRoutes.printCruise, (params) => {
      const _cruiseId = params.requireInt("cruiseId");
      return <PrintView />;
    });
    router.on(ShipRoutes.cruise, (params) => {
      const cruiseId = params.requireInt("cruiseId");
      return <CruiseView cruiseId={cruiseId} />;
    });
    router.on(ShipRoutes.edit, () => {
      return <ShipEditView cancelEdit={() => navigateShip(ShipRoutes.calendar)} key={ship.id} ship={ship} />;
    });
    router.on(ShipRoutes.print, () => {
      return <PrintView />;
    });
    router.on(ShipRoutes.focus, () => {
      return <></>;
    });
    router.on(ShipRoutes.sync, () => {
      return <ShipSyncStatusView />;
    });
    router.on(ShipRoutes.settings, () => {
      return <></>;
    });
    router.on(ShipRoutes.calendar, () => {
      return <CruiseCalendarView />;
    });
    router.on(ShipRoutes.import, () => {
      return <ShipImportView cancelEdit={() => navigateShip(ShipRoutes.calendar)} ship={ship} />;
    });
    router.on(ShipRoutes.unknown, () => {
      return <><div>Not found</div></>;
    });
  });

  const [overlay, overlayOptions, setOverlay, resetOverlay] = useOverlay<ShipViewOverlayContent, ShipViewOverlayOptions>(ShipViewOverlayContent.none);

  const state: ShipViewState = {
    focusActive: shipRouteName == ShipRoutes.focus,
    navigateShip: navigateShip,
    printMode: printMode,
    printRoot: printRoot,
    shipCloudEditable: app.cloudSession() && ship.clientToken == undefined && requireUserLevel("primary"),
    printTripId: printTripId,
    printDayId: printDayId,
    shipOverlay: overlay,
    shipOverlayOptions: overlayOptions || {},
    resetShipOverlay: resetOverlay,
    setShipOverlay: setOverlay,
    setPrinting: (mode: PrintMode, object?: PrintRoot, tripId?: number, dayId?: number) => {
      setPrintMode(mode);
      setPrintRoot(object);
      setPrintTripId(tripId);
      setPrintDayId(dayId);
      if (mode != PrintMode.none) {
        setPrintReturnRoute(shipRouteName);
        setPrintReturnParams(routeParams);
        navigateShip(ShipRoutes.print);
      } else {
        navigateShip(printReturnRoute, printReturnParams);
      }
    },
    shipRoute: route,
    shipRouteName: shipRouteName,
    shipUsers: users,
    actingShipUser: actingShipUser,
    setActingShipUser: setActingShipUser,
  };

  useEffect(() => {
    const syncStatus = status?.sync?.status;
    const syncSettled = syncStatus == ILogbookSyncStatus.current
      || syncStatus == ILogbookSyncStatus.dataWaiting
      || syncStatus == ILogbookSyncStatus.paused;
    if (!app.cloudSession() && app.current && !app.current.initialSyncCompleted && !syncSettled && shipRouteName != ShipRoutes.sync) {
      navigateShip(ShipRoutes.sync);
    }
    if (shipRouteName == ShipRoutes.settings) {
      setOverlay(ShipViewOverlayContent.shipSettings);
      navigateShip(ShipRoutes.calendar);
    }
    if (shipRouteName == ShipRoutes.userSwitch) {
      if (app.hasFeature("multi-user")) {
        setOverlay(ShipViewOverlayContent.userSwitch);
      }
      navigateShip(ShipRoutes.calendar);
    }
    if (shipRouteName == ShipRoutes.unknown && app.shipId) {
      navigateShip(ShipRoutes.calendar);
    } else if (shipRouteName == ShipRoutes.unknown && !app.shipId) {
      navigateApp(AppRoutes.accountSettings);
    }
    if (!app.cloudSession() && app.shipId && shipId != app.shipId) {
      navigateApp(AppRoutes.accountSettings);
    }
  }, [shipRouteName, app.current]);

  if (!ship) {
    return (
      <div className="ShipView">
        <Loading />
      </div>
    );
  }

  return (
    <ShipViewContext.Provider value={state}>
      <div className="ShipView">
        <ShipOverlayContentView />
        {!app.cloudSession() && requireUserLevel("viewCurrent") && <RecordingClockWarning key={shipId} status={status?.nmea?.clock} />}
        <div className={"ShipView-content" + (nmeaConfigured ? " instruments" : "") + (instrumentsMinized ? " instruments-minized" : "")}>
          <div className="ShipContentView">
            {!app.cloudSession() && requireUserLevel("viewCurrent") && <FocusView show={state.focusActive} />}
            {content}
          </div>
          {nmeaConfigured && <InstrumentsView minimized={instrumentsMinized} setMinimized={setInstrumentsMinized} connected={nmeaConnected} />}
        </div>
      </div>
    </ShipViewContext.Provider>
  );
}

export { ShipRoutes, ShipViewRoutes } from "./Ship/Router"
export { ShipViewContext, GetShipView, GetShipUsers } from "./Ship/Context"
export type { ShipViewState } from "./Ship/Context"
